import { useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";

type ShortcutAction = () => void;

interface Shortcut {
  key: string;
  ctrl?: boolean;
  meta?: boolean;
  shift?: boolean;
  alt?: boolean;
  action: ShortcutAction;
  description: string;
}

const defaultShortcuts: Omit<Shortcut, 'action'>[] = [
  { key: "d", alt: true, description: "Go to Dashboard" },
  { key: "t", alt: true, description: "Go to Teams" },
  { key: "s", alt: true, description: "Go to Staff Members" },
  { key: "r", alt: true, description: "Go to Review Cycles" },
  { key: "a", alt: true, description: "Go to Analytics" },
  { key: "c", alt: true, description: "Go to Calendar" },
  { key: "h", alt: true, description: "Go to Help Center" },
  { key: "n", alt: true, shift: true, description: "Create new (context-aware)" },
];

export function useKeyboardShortcuts(customShortcuts?: Shortcut[]) {
  const navigate = useNavigate();

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    // Don't trigger shortcuts when typing in inputs
    const target = event.target as HTMLElement;
    if (
      target.tagName === "INPUT" ||
      target.tagName === "TEXTAREA" ||
      target.contentEditable === "true"
    ) {
      return;
    }

    // Check custom shortcuts first
    if (customShortcuts) {
      for (const shortcut of customShortcuts) {
        if (matchesShortcut(event, shortcut)) {
          event.preventDefault();
          shortcut.action();
          return;
        }
      }
    }

    // Built-in navigation shortcuts (Alt + key)
    if (event.altKey && !event.ctrlKey && !event.metaKey) {
      switch (event.key.toLowerCase()) {
        case "d":
          event.preventDefault();
          navigate("/dashboard");
          break;
        case "t":
          event.preventDefault();
          navigate("/teams");
          break;
        case "s":
          if (!event.shiftKey) {
            event.preventDefault();
            navigate("/staff-members");
          }
          break;
        case "r":
          event.preventDefault();
          navigate("/cycles");
          break;
        case "a":
          event.preventDefault();
          navigate("/analytics");
          break;
        case "c":
          event.preventDefault();
          navigate("/calendar");
          break;
        case "h":
          event.preventDefault();
          navigate("/help-center");
          break;
      }
    }

    // Escape key handling
    if (event.key === "Escape") {
      // Close any open dialogs/modals - handled by Radix components
      // Could emit custom event for other components
      document.dispatchEvent(new CustomEvent("app:escape"));
    }
  }, [navigate, customShortcuts]);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);
}

function matchesShortcut(event: KeyboardEvent, shortcut: Shortcut): boolean {
  return (
    event.key.toLowerCase() === shortcut.key.toLowerCase() &&
    !!event.ctrlKey === !!shortcut.ctrl &&
    !!event.metaKey === !!shortcut.meta &&
    !!event.shiftKey === !!shortcut.shift &&
    !!event.altKey === !!shortcut.alt
  );
}

export function getShortcutsList(): { key: string; description: string }[] {
  return [
    { key: "⌘/Ctrl + K", description: "Open command palette" },
    { key: "Alt + D", description: "Go to Dashboard" },
    { key: "Alt + T", description: "Go to Teams" },
    { key: "Alt + S", description: "Go to Staff Members" },
    { key: "Alt + R", description: "Go to Review Cycles" },
    { key: "Alt + A", description: "Go to Analytics" },
    { key: "Alt + C", description: "Go to Calendar" },
    { key: "Alt + H", description: "Go to Help Center" },
    { key: "Escape", description: "Close dialogs/modals" },
  ];
}
